// The API epoch (docs/Y2026-Q4/design-api-epoch.md). A bundle's epoch is the
// <meta name="api-epoch"> in the index.html it shipped with, which must equal API_EPOCH
// in src/inrange_agents/lambda_handlers/api/api_epoch.py (a test holds them equal). The
// API refuses an older epoch with a 426: the tab then keeps what it has loaded, refuses
// to write, and shows that an update is required.
import { useSyncExternalStore } from "react";

export const API_EPOCH_HEADER = "X-Api-Epoch";

export const epochOf = (doc: Document): number | null => {
  const value = doc.querySelector<HTMLMetaElement>('meta[name="api-epoch"]')?.content;
  const epoch = Number(value);
  return value && Number.isInteger(epoch) ? epoch : null;
};

// Null sends no header, so a page missing its tag is served rather than refused.
export const API_EPOCH = epochOf(document);

export class ClientOutdatedError extends Error {
  readonly status = 426;
}

export const isClientOutdated = (error: unknown): boolean =>
  error instanceof ClientOutdatedError;

let outdated = false;
const listeners = new Set<() => void>();

export const markClientOutdated = () => {
  if (outdated) return;
  outdated = true;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useClientOutdated = (): boolean =>
  useSyncExternalStore(subscribe, () => outdated);

// A query the API refused waits out the reload instead of failing: it keeps its last
// data and fetches nothing more, so no page drops into an error state.
const HOLD_MS = 2 ** 31 - 1;

export const retryUnlessOutdated =
  (times: number) =>
  (failureCount: number, error: unknown): boolean =>
    isClientOutdated(error) || failureCount < times;

export const retryDelayHoldingOutdated = (failureCount: number, error: unknown): number =>
  isClientOutdated(error) ? HOLD_MS : Math.min(1000 * 2 ** failureCount, 30_000);
